import { useGetPreExamSettingsFromExamCourseSettingsByExamCourseCodeQuery } from '@exam-center/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Flex,
  FormRadio,
  PaperSection,
  SectionTitle,
  Text,
  Grid,
  FormTimeValueInput,
  WatchLaterOutlinedIcon,
  Tooltip,
  CircleIcon,
  Button,
  AddIcon,
  Divider,
  Form,
  ConfirmationModal,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';
import { formatSecondsToHoursMinutes } from '@shared/utils';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { FormExamDurationSessionTimeInput } from '../../components';
import { usePreExamSettingsFormLogic, usePreExamSettingsFormSubmission } from '../../hooks/';

import { PreExamSettingsSectionFormSchema } from './schema';
import {
  type PreExamSettingsSectionFormInputs,
  type PreExamSettingsSectionFormOutputs,
} from './types';
import { getAddSessionGuidingText } from './utils';

export interface PreExamSettingsSectionFormProps {
  slotProps: {
    multipleSession: {
      minSession: number;
      maxSession: number;
    };
  };
  examCourseId: string;
  examCourseCode: string;
  onFormDirtyChange?: (isDirty: boolean) => void;
}

export default function PreExamSettingsSectionForm(
  props: Readonly<PreExamSettingsSectionFormProps>,
) {
  const { examCourseId, examCourseCode, slotProps, onFormDirtyChange } = props;

  const { minSession, maxSession } = slotProps.multipleSession;

  const { showPromiseSnackbar } = useSnackbar();

  const [isExamCourseSettingsConfirmationModalOpen, examCourseSettingsConfirmationModalHandlers] =
    useDisclosure();
  const {
    open: openExamCourseSettingsConfirmationModal,
    close: closeExamCourseSettingsConfirmationModal,
  } = examCourseSettingsConfirmationModalHandlers;

  const { data: examCourseData } = useGetPreExamSettingsFromExamCourseSettingsByExamCourseCodeQuery(
    {
      variables: {
        getExamCourseByExamCourseCodeInput: {
          examCourseCode,
        },
      },
      skip: !examCourseCode,
    },
  );

  const examDurationSettings =
    examCourseData?.ecGetExamCourseByExamCourseCode.examCourse.settings.examDuration;

  const formContext = useForm<
    PreExamSettingsSectionFormInputs,
    unknown,
    PreExamSettingsSectionFormOutputs
  >({
    resolver: zodResolver(PreExamSettingsSectionFormSchema),
  });

  const {
    visibleFields,
    fields,
    handleAddSession,
    handleRemoveSession,
    totalSessionTime,
    canAddNewSession,
    errors,
    multipleSessions,
  } = usePreExamSettingsFormLogic(formContext, examDurationSettings, minSession, maxSession);

  const isFormDirty = formContext.formState.isDirty;

  useEffect(() => {
    onFormDirtyChange?.(isFormDirty);
  }, [isFormDirty, onFormDirtyChange]);

  const { handleFormSubmit, isSubmitting } = usePreExamSettingsFormSubmission({
    examCourseId,
    examCourseCode,
    onCompleted: () => {
      closeExamCourseSettingsConfirmationModal();
    },
  });

  const onSubmit = () => {
    openExamCourseSettingsConfirmationModal();
  };

  const handleConfirm = () => {
    showPromiseSnackbar({
      message: {
        pending: 'กำลังบันทึก',
        success: 'บันทึกข้อมูลเรียบร้อยแล้ว',
        error: 'ไม่สามารถบันทึกข้อมูลได้',
      },
      promise: () => handleFormSubmit(formContext.getValues()),
    });
  };

  const handleCancel = () => {
    closeExamCourseSettingsConfirmationModal();
  };

  return (
    <>
      <FormProvider {...formContext}>
        <PaperSection title='Pre-Exam Settings'>
          <Form onSubmit={formContext.handleSubmit(onSubmit)}>
            <Flex
              sx={(theme) => ({
                flexDirection: 'column',
                p: theme.spacing(6),
                gap: theme.spacing(6),
              })}
            >
              <SectionTitle title='Exam Duration' />
              <Grid sx={{ gap: (theme) => theme.spacing(6), gridTemplate: '1fr / 240px 1fr' }}>
                <Box>
                  <Text variant='body1Semibold'>ระยะเวลาทำข้อสอบ</Text>
                </Box>
                <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
                  {/* NOTE: FormRadioSelection is not support the description, so we have to use plain FormRadio instead */}
                  {/* Single session radio */}
                  <Flex sx={{ flexDirection: 'column' }}>
                    <FormRadio
                      name='examDuration.type'
                      label='กำหนดระยะเวลาสอบแบบ 1 ช่วง'
                      value='single-session'
                    />
                    <Flex
                      sx={(theme) => ({
                        flexDirection: 'column',
                        ml: theme.spacing(8),
                        gap: theme.spacing(2.5),
                      })}
                    >
                      <Text variant='caption' color='textSecondary'>
                        ใช้สำหรับหลักสูตรที่มีการสอบเพียงครั้งเดียวโดยไม่แบ่งช่วงเวลา
                      </Text>
                      {visibleFields.singleSessionDuration ? (
                        <Box sx={{ width: '256px' }}>
                          <FormTimeValueInput
                            name='examDuration.singleSessionValue'
                            label='ระยะเวลาสอบ (HH:MM)'
                            unit='seconds'
                            required
                          />
                        </Box>
                      ) : null}
                    </Flex>
                  </Flex>
                  {/* Multiple session radio */}
                  <Flex sx={{ flexDirection: 'column' }}>
                    <FormRadio
                      name='examDuration.type'
                      label={`กำหนดระยะเวลาสอบแบบหลายช่วง (สูงสุด ${maxSession} ช่วง)`}
                      value='multiple-session'
                    />
                    <Flex
                      sx={(theme) => ({
                        flexDirection: 'column',
                        ml: theme.spacing(8),
                        gap: theme.spacing(2.5),
                      })}
                    >
                      <Text variant='caption' color='textSecondary'>
                        เหมาะสำหรับหลักสูตรที่แบ่งการสอบออกเป็นหลายช่วงเวลา
                      </Text>
                      {visibleFields.multipleSessionDuration ? (
                        <Flex
                          sx={(theme) => ({
                            flexDirection: 'column',
                            gap: theme.spacing(4),
                          })}
                        >
                          {fields.map((field, order) => {
                            return (
                              <FormExamDurationSessionTimeInput
                                key={field.id}
                                name={`examDuration.multipleSessionValue.${order}.value`}
                                order={order + 1}
                                onRemove={
                                  order + 1 > minSession
                                    ? () => {
                                        handleRemoveSession(order);
                                      }
                                    : undefined
                                }
                              />
                            );
                          })}
                          <Flex
                            sx={(theme) => ({
                              p: theme.spacing(3, 4),
                              gap: theme.spacing(2),
                              border: `1px solid ${theme.palette.grey[300]}`,
                              borderRadius: theme.shape.rounded(2),
                              backgroundColor: theme.palette.grey[50],
                            })}
                          >
                            <WatchLaterOutlinedIcon sx={{ fontSize: '24px' }} />
                            <Text variant='body1Semibold'>
                              รวมระยะเวลาสอบ:{' '}
                              {formatSecondsToHoursMinutes(totalSessionTime, 'TEXT')}
                            </Text>
                          </Flex>
                          {errors.multipleSessions?.root?.message ? (
                            <Text
                              variant='body2'
                              sx={{ color: (theme) => theme.palette.error.main }}
                            >
                              {errors.multipleSessions.root.message}
                            </Text>
                          ) : null}
                          <Tooltip
                            title={getAddSessionGuidingText(
                              multipleSessions,
                              minSession,
                              maxSession,
                            )}
                          >
                            <Box sx={{ width: '150px' }}>
                              <Button
                                startIcon={<AddIcon />}
                                variant='outlined'
                                color='secondary'
                                size='large'
                                onClick={handleAddSession}
                                disabled={!canAddNewSession}
                              >
                                เพิ่มช่วงสอบ
                              </Button>
                            </Box>
                          </Tooltip>
                        </Flex>
                      ) : null}
                      {errors.examDurationType?.message ? (
                        <Text variant='body2' sx={{ color: (theme) => theme.palette.error.main }}>
                          {errors.examDurationType.message}
                        </Text>
                      ) : null}
                    </Flex>
                  </Flex>
                </Flex>
              </Grid>
            </Flex>
            <Divider />
            <Flex
              sx={(theme) => ({
                p: theme.spacing(4, 6),
                gap: theme.spacing(4),
                alignItems: 'center',
                justifyContent: 'right',
              })}
            >
              <Flex
                sx={{
                  flexDirection: 'column',
                  gap: (theme) => theme.spacing(2),
                  alignItems: 'flex-end',
                }}
              >
                <Text variant='caption'>
                  การเปลี่ยนแปลงมีผลกับรอบสอบที่ยังไม่ได้มอบหมายชุดข้อสอบ
                </Text>
                {isFormDirty ? (
                  <Text variant='caption' color='text.secondary'>
                    <CircleIcon
                      sx={{
                        fontSize: '8px',
                        color: (theme) => theme.palette.primary.main,
                      }}
                    />{' '}
                    มีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก
                  </Text>
                ) : null}
              </Flex>
              <Button type='submit' disabled={!isFormDirty} loading={isSubmitting}>
                บันทึก
              </Button>
            </Flex>
          </Form>
        </PaperSection>
      </FormProvider>
      <ConfirmationModal
        open={isExamCourseSettingsConfirmationModalOpen}
        title='บันทึกการตั้งค่า?'
        content={
          <Text>
            การเปลี่ยนแปลงจะมีผลกับ{' '}
            <Text component='span' variant='body1Semibold'>
              “รอบสอบที่ยังไม่ได้มอบหมายชุดข้อสอบ”
            </Text>{' '}
            โดยอัตโนมัติ
          </Text>
        }
        onAccept={handleConfirm}
        onClose={handleCancel}
        onDiscard={handleCancel}
        loading={isSubmitting}
        acceptText='บันทึก'
        discardText='ยกเลิก'
      />
    </>
  );
}
